import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, GraduationCap, ChevronRight } from "lucide-react";
import InstructorBottomNav from "../components/instructor/InstructorBottomNav";
import InstructorTrainingVideoCard from "../components/instructor/InstructorTrainingVideoCard";
import { CoachingAlertsCard } from "../components/instructor/CoachingAlertsCard";

/**
 * InstructorTraining — Phase 11.2 (Feb 6, 2026): the "Training" tab.
 *
 * Skill development only. Mastery, scenarios, training videos,
 * coaching alerts, CPD, community.
 *
 * Per locked map: NO booking, NO progress tracking. Those are Work
 * and Progress respectively.
 */
export default function InstructorTraining() {
  const { user } = useAuth();
  const nav = useNavigate();
  const role = (user?.role || "adi").toLowerCase();
  const isPdi = role === "pdi";

  return (
    <div data-testid="instructor-training-page" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="training-back-btn" onClick={() => nav(isPdi ? "/pdi-hub" : "/instructor")} className="text-slate-400 hover:text-slate-700 dark:hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <GraduationCap className="w-5 h-5 text-emerald-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-base">Training</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        <InstructorTrainingVideoCard isAdmin={role === "admin"} />
        <CoachingAlertsCard />

        <section data-testid="training-skills-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Skill builders</h2>
          <Tile testid="training-tile-mastery"         label="Mastery Builder"   desc="Build every skill properly"          onClick={() => nav("/instructor/mastery")} />
          <Tile testid="training-tile-practice-review" label="Practice review"   desc="AI-reviewed lesson clips"            onClick={() => nav("/lesson-review")} />
          <Tile testid="training-tile-cpd"             label="CPD record"        desc="Auto-issued certificates · log hours" onClick={() => nav("/cpd")} />
          <Tile testid="training-tile-community"       label="Community"         desc="Peer discussion · ask & share"       onClick={() => nav("/community")} />
        </section>

        {isPdi && (
          <section data-testid="training-pdi-section" className="space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Part 3 prep</h2>
            <Tile testid="training-tile-pdi-hub"   label="PDI Hub overview" desc="Full PDI funnel — readiness, weak areas, focus" onClick={() => nav("/pdi-hub")} />
            <Tile testid="training-tile-mock-part3" label="Mock Part 3"    desc="30-min simulated assessment"                     onClick={() => nav("/pdi-mode/mock-part3")} />
          </section>
        )}
      </main>

      <InstructorBottomNav role={role} active="training" />
    </div>
  );
}

function Tile({ testid, label, desc, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-center gap-3 hover:border-emerald-300 active:scale-[0.99] transition-all">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}
